import { Alert, Badge, Button, EmptyState, FieldBlock, Input, LoadingState, Modal, Select, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { ColorField, SwitchCard, normalizarCodigo } from './catalog-shared.tsx'

/**
 * Cadastro de Status de Item de Trabalho (SPEC-20260815-0821), padrao
 * FormScreen. `isInitial` sugere a entrada de itens novos; `isTerminal` conta
 * o item como concluido — os dois viram interruptores com a explicacao do
 * efeito, em vez da "categoria" do prototipo (mapeia 1:1 ao schema).
 */

type StatusList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.catalog)['work-item-statuses']['$get']>>['json']>>
type StatusRow = StatusList['statuses'][number]

type FormStatus = {
  namePt: string
  nameEn: string
  code: string
  color: string | null
  isInitial: boolean
  isTerminal: boolean
  isActive: boolean
  sortOrder: number
}

const VAZIO: FormStatus = {
  namePt: '',
  nameEn: '',
  code: '',
  color: null,
  isInitial: false,
  isTerminal: false,
  isActive: true,
  sortOrder: 0,
}

export function StatusesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({
    queryKey: ['work-item-statuses'],
    queryFn: async () => (await api.catalog['work-item-statuses'].$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormStatus>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['work-item-statuses'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    void qc.invalidateQueries({ queryKey: ['i18n'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const corpoDe = (f: FormStatus) => ({
    code: normalizarCodigo(f.code || f.namePt),
    name: { 'pt-BR': f.namePt.trim(), ...(f.nameEn.trim() ? { 'en-US': f.nameEn.trim() } : {}) },
    color: f.color,
    isInitial: f.isInitial,
    isTerminal: f.isTerminal,
    isActive: f.isActive,
    sortOrder: f.sortOrder,
  })

  const criar = useMutation({
    mutationFn: async (f: FormStatus) => (await api.catalog['work-item-statuses'].$post({ json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Status criado.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormStatus }) =>
      (await api.catalog['work-item-statuses'][':id'].$patch({ param: { id }, json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Status atualizado.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog['work-item-statuses'][':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  /**
   * Exclusão com transferência: quando o status tem itens/lanes, o modal pede
   * para qual status transferir e exige digitar EXCLUIR (2026-08-29).
   */
  const [excluindo, setExcluindo] = useState<{ id: string; nome: string; uso: number } | null>(null)
  const [destinoId, setDestinoId] = useState<string | null>(null)
  const [confirmacaoTexto, setConfirmacaoTexto] = useState('')

  const excluir = useMutation({
    mutationFn: async ({ id, moverPara }: { id: string; moverPara?: string }) =>
      (
        await api.catalog['work-item-statuses'][':id'].$delete({
          param: { id },
          query: moverPara ? { moverPara } : {},
        })
      ).json(),
    onSuccess: (dados) => {
      invalidar()
      setConfirmandoExclusao(null)
      setExcluindo(null)
      setDestinoId(null)
      setConfirmacaoTexto('')
      const movidos = (dados as { movidos?: number }).movidos ?? 0
      toast.push({
        tone: 'success',
        message: movidos > 0 ? `Status excluído e ${movidos} registro(s) transferido(s).` : 'Status excluído.',
      })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const statuses = lista.data?.statuses ?? []
  const emEdicao = editando?.id ? statuses.find((s) => s.id === editando.id) : undefined

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return statuses
    return statuses.filter((s) => `${s.name['pt-BR'] ?? ''} ${s.code}`.toLowerCase().includes(termo))
  }, [statuses, busca])

  const abrir = (alvo: StatusRow | null) => {
    setForm(
      alvo
        ? {
            namePt: alvo.name['pt-BR'] ?? '',
            nameEn: alvo.name['en-US'] ?? '',
            code: alvo.code,
            color: alvo.color,
            isInitial: alvo.isInitial,
            isTerminal: alvo.isTerminal,
            isActive: alvo.isActive,
            sortOrder: alvo.sortOrder,
          }
        : { ...VAZIO, sortOrder: (statuses.at(-1)?.sortOrder ?? 0) + 10 },
    )
    setEditando({ id: alvo?.id ?? null })
  }

  const alterado =
    editando?.id == null
      ? form.namePt.trim().length >= 2
      : emEdicao !== undefined &&
        JSON.stringify(corpoDe(form)) !==
          JSON.stringify(
            corpoDe({
              namePt: emEdicao.name['pt-BR'] ?? '',
              nameEn: emEdicao.name['en-US'] ?? '',
              code: emEdicao.code,
              color: emEdicao.color,
              isInitial: emEdicao.isInitial,
              isTerminal: emEdicao.isTerminal,
              isActive: emEdicao.isActive,
              sortOrder: emEdicao.sortOrder,
            }),
          )

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const siglaDerivada = normalizarCodigo(form.code || form.namePt)
    const salvarValido = alterado && form.namePt.trim().length >= 2 && !criar.isPending && !atualizar.isPending

    return (
      <FormScreen
        title={editando.id ? 'Editar status' : 'Novo status'}
        subtitle={editando.id ? (emEdicao?.name['pt-BR'] ?? emEdicao?.code) : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={editando.id ? 'Tudo salvo' : 'Preencha o nome para salvar'}
        saveLabel={editando.id ? 'Salvar' : 'Criar status'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        // Exclusão no rodapé, à esquerda (padrão 2026-08-30). Status EM USO cai
        // no mesmo fluxo da lista: escolher o destino e confirmar digitando.
        onDelete={
          emEdicao
            ? () => {
                const nome = emEdicao.name['pt-BR'] ?? emEdicao.code
                if (emEdicao.usageCount > 0) {
                  setExcluindo({ id: emEdicao.id, nome, uso: emEdicao.usageCount })
                  setDestinoId(null)
                  setConfirmacaoTexto('')
                  return
                }
                if (!confirm(`Excluir o status “${nome}”?`)) return
                excluir.mutate({ id: emEdicao.id }, { onSuccess: () => setEditando(null) })
              }
            : undefined
        }
        deleteLabel="Excluir status"
        deleteHint={
          (emEdicao?.usageCount ?? 0) > 0
            ? 'Excluir — os registros serão transferidos para outro status'
            : undefined
        }
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Informações do status">
          <FormRow cols="2fr 2fr 1fr">
            <FieldBlock label="Nome do status" required hint="Como aparece nas telas em português.">
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.namePt}
                  placeholder="Ex.: Em revisão"
                  onChange={(e) => setForm((f) => ({ ...f, namePt: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Name (en-US)" hint="Opcional — para quem usa a plataforma em inglês.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.nameEn}
                  placeholder="Ex.: In review"
                  onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock
              label="Código"
              hint={
                form.code.trim() === '' && form.namePt.trim() !== ''
                  ? `Derivado do nome: ${siglaDerivada}. Digite para personalizar.`
                  : 'Identificador técnico, único no workspace.'
              }
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.code}
                  placeholder={siglaDerivada || 'Ex.: IN_REVIEW'}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: f.code ? normalizarCodigo(f.code) : f.code }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          <FormRow cols="2fr 1fr">
            <ColorField value={form.color} onChange={(color) => setForm((f) => ({ ...f, color }))} />
            <FieldBlock label="Ordem" hint="Menor aparece primeiro nas listas e seletores.">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  min={0}
                  value={String(form.sortOrder)}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Math.max(0, Number(e.target.value) || 0) }))}
                />
              )}
            </FieldBlock>
          </FormRow>
        </FormSection>

        <FormSection title="Comportamento no fluxo">
          <FormRow cols="2fr 1fr">
            <FieldBlock
              label="Fase no fluxo"
              hint="Escolha UMA: um status é inicial, em processo ou final."
            >
              {() => {
                const fase: 'inicial' | 'processo' | 'final' = form.isInitial
                  ? 'inicial'
                  : form.isTerminal
                    ? 'final'
                    : 'processo'
                const definir = (v: 'inicial' | 'processo' | 'final') =>
                  setForm((f) => ({ ...f, isInitial: v === 'inicial', isTerminal: v === 'final' }))
                const opcoes = [
                  { v: 'inicial', r: 'Inicial', i: 'fas fa-flag', d: 'Entrada para Itens de Trabalho novos.' },
                  { v: 'processo', r: 'Em processo', i: 'fas fa-arrows-rotate', d: 'Item em andamento no fluxo.' },
                  { v: 'final', r: 'Final', i: 'fas fa-flag-checkered', d: 'Conclui o item nas métricas.' },
                ] as const
                return (
                  <div className="adm-fase" role="radiogroup" aria-label="Fase no fluxo">
                    {opcoes.map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        role="radio"
                        aria-checked={fase === o.v}
                        className={`adm-fase__op${fase === o.v ? ' adm-fase__op--on' : ''}`}
                        onClick={() => definir(o.v)}
                      >
                        <span className="adm-fase__mark" aria-hidden />
                        <i className={`${o.i} adm-fase__icone`} aria-hidden />
                        <span className="adm-fase__txt">
                          <span className="adm-fase__nome">{o.r}</span>
                          <span className="adm-fase__desc">{o.d}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )
              }}
            </FieldBlock>
            <SwitchCard
              title={form.isActive ? 'Status ativo' : 'Status inativo'}
              description={form.isActive ? 'Aparece nas opções e pode ser mapeado nos quadros.' : 'Some das opções; registros existentes ficam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>
          {emEdicao ? (
            <p className="adm-hint">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.usageCount > 0
                ? `Em uso por ${emEdicao.usageCount} ${emEdicao.usageCount === 1 ? 'registro' : 'registros'} (itens e lanes de quadro).`
                : 'Ainda sem uso.'}
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Status</h1>
        <p className="adm-head__desc">Status mapeáveis nas lanes dos quadros — inicial sugere a entrada, terminal conta como concluído.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando os status" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os status"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por nome ou código…" aria-label="Filtrar status"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {statuses.length} {statuses.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-traffic-light"
            title={statuses.length === 0 ? 'Nenhum status ainda' : 'Nenhum status com esse filtro'}
            description={statuses.length === 0
              ? 'Crie ao menos um status inicial e um terminal para o fluxo funcionar.'
              : `Nada casa com “${busca}”.`}
            action={statuses.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Novo status</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const nome = row.name['pt-BR'] ?? row.code
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon" style={row.color ? { color: row.color } : undefined}>
                  <i className="fas fa-circle" aria-hidden />
                </span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{nome}</span>
                  <span className="adm-catrow__sub">
                    Código {row.code}
                    {row.isInitial ? ' · inicial' : ''}
                    {row.isTerminal ? ' · terminal' : ''}
                    {row.usageCount > 0 ? ` · em uso por ${row.usageCount} ${row.usageCount === 1 ? 'registro' : 'registros'}` : ' · sem uso'}
                  </span>
                </span>
                {/* status nao tem mais "do sistema": todos sao editaveis e
                    excluiveis (2026-08-29) */}

                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{nome}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate({ id: row.id })}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativo' : 'Inativo'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${nome} está ativo`}
                        title={row.isActive ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${nome}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                      aria-label={`Excluir ${row.code}`}
                      title={row.usageCount > 0 ? 'Excluir — os registros serão transferidos para outro status' : 'Excluir'}
                      onClick={() => {
                        // em uso: modal pede o destino + confirmação digitada
                        if (row.usageCount > 0) {
                          setExcluindo({ id: row.id, nome, uso: row.usageCount })
                          setDestinoId(null)
                          setConfirmacaoTexto('')
                        } else setConfirmandoExclusao(row.id)
                      }}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Exclusão de status EM USO: escolhe o destino dos registros e exige
          digitar EXCLUIR (pedido 2026-08-29) */}
      {excluindo ? (
        <Modal
          open
          onClose={() => setExcluindo(null)}
          title={`Excluir o status “${excluindo.nome}”`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setExcluindo(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={excluir.isPending}
                disabled={!destinoId || confirmacaoTexto.trim().toUpperCase() !== 'EXCLUIR'}
                onClick={() => excluir.mutate({ id: excluindo.id, moverPara: destinoId ?? undefined })}
              >
                Excluir e transferir
              </Button>
            </>
          }
        >
          <Alert tone="warning">
            {excluindo.uso} registro(s) usam este status (Itens de Trabalho e/ou lanes de quadros). Escolha para qual
            status eles serão transferidos — a mudança é aplicada antes da exclusão e não pode ser desfeita.
          </Alert>
          <FieldBlock label="Transferir os registros para" required>
            {({ id }) => (
              <Select
                id={id}
                value={destinoId}
                placeholder="Escolha o status de destino"
                options={statuses
                  .filter((s) => s.id !== excluindo.id)
                  .map((s) => ({ value: s.id, label: s.name['pt-BR'] ?? s.code }))}
                onChange={(v) => setDestinoId(v)}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Digite EXCLUIR para confirmar" required hint="Confirmação consciente: a ação é definitiva.">
            {({ id }) => (
              <Input
                id={id}
                value={confirmacaoTexto}
                placeholder="EXCLUIR"
                autoComplete="off"
                onChange={(e) => setConfirmacaoTexto(e.target.value)}
              />
            )}
          </FieldBlock>
        </Modal>
      ) : null}
    </>
  )
}
